import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Trash2, Pencil, Sparkles, X, Check,
  ShoppingBag, Package, MessageSquare, Users, Shield,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api } from "../lib/api";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";

export default function AdminPanelPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ products: [], requests: [], offers: [], users: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null); // {kind, item}

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/all");
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user === null) navigate("/login");
    else if (user && user.role !== "admin") {
      toast.error("Admin access required");
      navigate("/");
    } else if (user && user.role === "admin") {
      load();
    }
  }, [user]);

  const remove = async (kind, id) => {
    if (!window.confirm("Delete this permanently?")) return;
    setBusy(`del-${id}`);
    try {
      await api.delete(`/admin/${kind}/${id}`);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Failed");
    } finally {
      setBusy(null);
    }
  };

  const toggleSponsor = async (kind, item) => {
    setBusy(`spo-${item.id}`);
    try {
      if (item.is_sponsored) {
        await api.delete(`/admin/${kind}/${item.id}/sponsor`);
        toast.success("Sponsor removed");
      } else {
        await api.post(`/admin/${kind}/${item.id}/sponsor`, { days: 7 });
        toast.success("Sponsored for 7 days");
      }
      await load();
    } catch {
      toast.error("Failed");
    } finally {
      setBusy(null);
    }
  };

  if (!user || loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-6 sm:py-10 md:py-14">
      <button
        onClick={() => navigate("/")}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 sm:mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        Home
      </button>

      <div className="mb-6 sm:mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 text-destructive px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-bold mb-3">
            <Shield className="h-3 w-3" />
            Admin panel
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
            Moderation console.
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground">
            Edit or delete any post, and toggle Sponsored ads across the marketplace.
          </p>
        </div>
      </div>

      <Tabs defaultValue="products" className="w-full">
        <TabsList className="bg-white border border-border rounded-md p-1 h-auto w-full sm:w-auto flex-nowrap overflow-x-auto scrollbar-none justify-start">
          <TabsTrigger data-testid="tab-admin-products" value="products" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <ShoppingBag className="h-3.5 w-3.5" /> Products
            <span className="font-mono-brand text-[10px] opacity-70">{data.products.length}</span>
          </TabsTrigger>
          <TabsTrigger data-testid="tab-admin-requests" value="requests" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <Package className="h-3.5 w-3.5" /> Buy requests
            <span className="font-mono-brand text-[10px] opacity-70">{data.requests.length}</span>
          </TabsTrigger>
          <TabsTrigger data-testid="tab-admin-offers" value="offers" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <MessageSquare className="h-3.5 w-3.5" /> Offers
            <span className="font-mono-brand text-[10px] opacity-70">{data.offers.length}</span>
          </TabsTrigger>
          <TabsTrigger data-testid="tab-admin-users" value="users" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <Users className="h-3.5 w-3.5" /> Users
            <span className="font-mono-brand text-[10px] opacity-70">{data.users.length}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-6">
          <ItemTable items={data.products} kind="products" onRemove={remove} onToggleSponsor={toggleSponsor} onEdit={setEditing} busy={busy}
            columns={["title", "seller_name", "price", "category", "sale_type"]} testPrefix="admin-product" onOpen={(p) => navigate(`/p/${p.id}`)} />
        </TabsContent>
        <TabsContent value="requests" className="mt-6">
          <ItemTable items={data.requests} kind="requests" onRemove={remove} onToggleSponsor={toggleSponsor} onEdit={setEditing} busy={busy}
            columns={["title", "buyer_name", "budget", "category", "quantity"]} testPrefix="admin-request" onOpen={(r) => navigate(`/r/${r.id}`)} />
        </TabsContent>
        <TabsContent value="offers" className="mt-6">
          <ItemTable items={data.offers} kind="offers" onRemove={remove} onToggleSponsor={toggleSponsor} onEdit={setEditing} busy={busy}
            columns={["description", "seller_name", "price", "availability"]} testPrefix="admin-offer" onOpen={(o) => navigate(`/r/${o.request_id}`)} />
        </TabsContent>
        <TabsContent value="users" className="mt-6">
          <UsersTable users={data.users} />
        </TabsContent>
      </Tabs>

      {editing && (
        <EditModal
          kind={editing.kind}
          item={editing.item}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function ItemTable({ items, kind, onRemove, onToggleSponsor, onEdit, busy, columns, testPrefix, onOpen }) {
  if (items.length === 0) {
    return (
      <div className="rounded-md border-2 border-dashed border-border bg-white p-8 text-sm text-center text-muted-foreground">
        No items yet.
      </div>
    );
  }
  return (
    <>
      {/* Mobile card list */}
      <div className="lg:hidden space-y-3">
        {items.map((it) => (
          <div
            key={it.id}
            data-testid={`${testPrefix}-row-${it.id}`}
            className="rounded-md border border-border bg-white p-4 space-y-3"
          >
            <button onClick={() => onOpen(it)} className="w-full text-left">
              <div className="font-semibold leading-tight line-clamp-2">
                {String(it[columns[0]] ?? "—")}
              </div>
              {it.is_sponsored && (
                <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-accent/20 text-accent px-1.5 py-0.5 text-[9px] font-bold uppercase">
                  <Sparkles className="h-2 w-2" /> Sponsored
                </span>
              )}
            </button>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              {columns.slice(1).map((c) => (
                <div key={c} className="min-w-0">
                  <div className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground">
                    {c.replace(/_/g, " ")}
                  </div>
                  <div className="truncate">{String(it[c] ?? "—")}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1.5 pt-2 border-t border-border">
              <Button
                size="sm" variant="outline"
                onClick={() => onToggleSponsor(kind, it)}
                disabled={busy === `spo-${it.id}`}
                data-testid={`${testPrefix}-sponsor-${it.id}`}
                className="rounded-md gap-1 min-h-[44px] flex-1 text-xs"
              >
                {busy === `spo-${it.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                {it.is_sponsored ? "Unsponsor" : "Sponsor"}
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => onEdit({ kind, item: it })}
                data-testid={`${testPrefix}-edit-${it.id}`}
                className="rounded-md gap-1 min-h-[44px] flex-1 text-xs"
              >
                <Pencil className="h-3 w-3" /> Edit
              </Button>
              <Button
                size="sm" variant="destructive"
                onClick={() => onRemove(kind, it.id)}
                disabled={busy === `del-${it.id}`}
                data-testid={`${testPrefix}-delete-${it.id}`}
                className="rounded-md gap-1 min-h-[44px] flex-1 text-xs"
              >
                {busy === `del-${it.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block rounded-md border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50">
            <tr>
              {columns.map((c) => (
                <th key={c} className="text-left px-4 py-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">
                  {c.replace(/_/g, " ")}
                </th>
              ))}
              <th className="text-right px-4 py-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr
                key={it.id}
                className="border-t border-border hover:bg-secondary/30 transition-colors"
              >
                {columns.map((c) => (
                  <td key={c} className="px-4 py-3 max-w-[240px]">
                    <button onClick={() => onOpen(it)} className="text-left truncate hover:text-primary">
                      <span className="truncate">{String(it[c] ?? "—")}</span>
                      {c === columns[0] && it.is_sponsored && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-accent/20 text-accent px-1.5 py-0.5 text-[9px] font-bold uppercase">
                          <Sparkles className="h-2 w-2" /> Sponsored
                        </span>
                      )}
                    </button>
                  </td>
                ))}
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => onToggleSponsor(kind, it)}
                      disabled={busy === `spo-${it.id}`}
                      className="rounded-md gap-1 h-8"
                    >
                      {busy === `spo-${it.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      {it.is_sponsored ? "Unsponsor" : "Sponsor 7d"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onEdit({ kind, item: it })} className="rounded-md gap-1 h-8">
                      <Pencil className="h-3 w-3" /> Edit
                    </Button>
                    <Button
                      size="sm" variant="destructive"
                      onClick={() => onRemove(kind, it.id)}
                      disabled={busy === `del-${it.id}`}
                      className="rounded-md gap-1 h-8"
                    >
                      {busy === `del-${it.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function UsersTable({ users }) {
  return (
    <div className="rounded-md border border-border bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50">
          <tr>
            <th className="text-left px-4 py-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Name</th>
            <th className="text-left px-4 py-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Email</th>
            <th className="text-left px-4 py-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Role</th>
            <th className="text-left px-4 py-3 text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Joined</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-border">
              <td className="px-4 py-3 font-semibold">{u.display_name}</td>
              <td className="px-4 py-3 font-mono-brand text-xs">{u.email}</td>
              <td className="px-4 py-3">
                {u.role === "admin" ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 text-destructive px-2 py-0.5 text-[10px] font-bold uppercase">
                    <Shield className="h-2.5 w-2.5" /> Admin
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">user</span>
                )}
              </td>
              <td className="px-4 py-3 text-xs text-muted-foreground">
                {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const EDITABLE_FIELDS = {
  products: ["title", "description", "category", "price", "moq", "stock", "sale_type"],
  requests: ["title", "description", "category", "budget", "quantity"],
  offers: ["description", "price", "availability"],
};

function EditModal({ kind, item, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const out = {};
    EDITABLE_FIELDS[kind].forEach((f) => (out[f] = item[f] ?? ""));
    return out;
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      EDITABLE_FIELDS[kind].forEach((f) => {
        const v = form[f];
        if (v !== "" && v !== item[f]) payload[f] = f === "quantity" ? parseInt(v) : v;
      });
      if (Object.keys(payload).length === 0) {
        toast.info("No changes");
        onClose();
        return;
      }
      await api.patch(`/admin/${kind}/${item.id}`, payload);
      toast.success("Saved");
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-edit-modal"
        className="bg-white rounded-md border border-border p-6 md:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-4"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1">
              Edit {kind.replace(/s$/, "")}
            </div>
            <h3 className="font-display text-xl font-semibold">{item.title || item.description?.slice(0, 40) || item.id}</h3>
          </div>
          <button type="button" onClick={onClose} className="h-8 w-8 rounded-full hover:bg-secondary flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>

        {EDITABLE_FIELDS[kind].map((f) => (
          <div key={f} className="space-y-1.5">
            <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
              {f.replace(/_/g, " ")}
            </Label>
            {f === "description" ? (
              <Textarea
                data-testid={`admin-edit-${f}`}
                value={form[f] || ""}
                onChange={(e) => set(f, e.target.value)}
                rows={3}
                className="rounded-md resize-none"
              />
            ) : (
              <Input
                data-testid={`admin-edit-${f}`}
                type={f === "quantity" ? "number" : "text"}
                value={form[f] || ""}
                onChange={(e) => set(f, e.target.value)}
                className="rounded-md h-10"
              />
            )}
          </div>
        ))}

        <div className="flex justify-end gap-2 pt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-md">Cancel</Button>
          <Button type="submit" data-testid="admin-edit-save" disabled={saving} className="rounded-md gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Save
          </Button>
        </div>
      </form>
    </div>
  );
}
